import { DAY_KEYS, type DayKey } from "@/lib/days";
import type {
  DailyRecurringTask,
  DailyRecurringTasksConfig,
  RecurringTask,
  RecurringTasksConfig,
  RecurringTasksDayData,
  RecurringTasksWeekData,
  WeeklyRecurringTask,
  WeeklyRecurringTasksConfig,
} from "@/lib/schema";

function dayIndex(dayKey: DayKey): number {
  return DAY_KEYS.indexOf(dayKey);
}

/** Daily reset: the tasks listing `dayKey`, which are that day's scoring set. */
export function tasksForDay(
  config: DailyRecurringTasksConfig,
  dayKey: DayKey,
): DailyRecurringTask[] {
  return config.tasks.filter((t) => t.visibleDays.includes(dayKey));
}

/**
 * A weekly task's opening day, reading through configs written before
 * `checkableFrom` existed. Those carry the old `visibleDays` set, whose
 * earliest member is exactly the first day the box used to appear — so the
 * legacy read and the daily→weekly conversion in the settings editor are the
 * same rule, and no migration is needed for live settings or sealed
 * snapshots alike.
 */
export function checkableFromOf(task: RecurringTask): DayKey {
  if ("checkableFrom" in task) return task.checkableFrom;
  return DAY_KEYS.find((k) => task.visibleDays.includes(k)) ?? "mon";
}

/** The instance's tasks with their opening days resolved. */
function weeklyTasks(
  config: WeeklyRecurringTasksConfig,
): WeeklyRecurringTask[] {
  return config.tasks.map((t) => ({
    id: t.id,
    label: t.label,
    checkableFrom: checkableFromOf(t),
  }));
}

/** Weekly reset: whether a task's box has opened by `dayKey`. */
export function isCheckableOn(
  task: WeeklyRecurringTask,
  dayKey: DayKey,
): boolean {
  return dayIndex(dayKey) >= dayIndex(checkableFromOf(task));
}

/** The first day any task opens — before it the widget does not render. */
export function earliestCheckableFrom(
  config: WeeklyRecurringTasksConfig,
): DayKey | null {
  let earliest: DayKey | null = null;
  for (const t of weeklyTasks(config)) {
    if (earliest === null || dayIndex(t.checkableFrom) < dayIndex(earliest)) {
      earliest = t.checkableFrom;
    }
  }
  return earliest;
}

/**
 * The last day any task opens — the earliest the set can possibly be
 * finished, since every task must be checked. Surfaced in the settings
 * Sammendrag so a set that cannot complete before Sunday says so.
 */
export function latestCheckableFrom(
  config: WeeklyRecurringTasksConfig,
): DayKey | null {
  let latest: DayKey | null = null;
  for (const t of weeklyTasks(config)) {
    if (latest === null || dayIndex(t.checkableFrom) > dayIndex(latest)) {
      latest = t.checkableFrom;
    }
  }
  return latest;
}

/** Weekly reset: the tasks rendered on `dayKey` — all of them, or none. */
export function weeklyTasksForDay(
  config: WeeklyRecurringTasksConfig,
  dayKey: DayKey,
): WeeklyRecurringTask[] {
  const earliest = earliestCheckableFrom(config);
  if (earliest === null) return [];
  return dayIndex(dayKey) >= dayIndex(earliest) ? weeklyTasks(config) : [];
}

function weeklyWeekData(weekData: unknown): RecurringTasksWeekData {
  return (weekData ?? { checked: {} }) as RecurringTasksWeekData;
}

/** Every task checked. An instance with no tasks is never finished. */
function isWeeklySetFinished(
  config: WeeklyRecurringTasksConfig,
  weekData: unknown,
): boolean {
  if (config.tasks.length === 0) return false;
  const data = weeklyWeekData(weekData);
  return config.tasks.every((t) => data.checked[t.id] === true);
}

/**
 * The award day (CONTEXT.md): the day a finished weekly set's points land
 * on, or null while the set is unfinished. `awardedOn` is written already
 * clamped to a day inside the week, so this only has to supply the fallback
 * — which covers both a week logged before award days were recorded and any
 * state where the stamp went missing. Sunday either way, so the points are
 * never silently dropped.
 */
export function awardDay(
  config: WeeklyRecurringTasksConfig,
  weekData: unknown,
): DayKey | null {
  if (!isWeeklySetFinished(config, weekData)) return null;
  return weeklyWeekData(weekData).awardedOn ?? "sun";
}

export function scoreDay(
  config: RecurringTasksConfig,
  dayData: unknown,
  dayKey: DayKey,
  weekData: unknown,
): number {
  if (config.reset === "weekly") {
    return awardDay(config, weekData) === dayKey ? config.points : 0;
  }
  const set = tasksForDay(config, dayKey);
  if (set.length === 0) return 0;
  const data = (dayData ?? { checked: {} }) as RecurringTasksDayData;
  const allChecked = set.every((t) => data.checked[t.id] === true);
  return allChecked ? config.points : 0;
}

export function scoreWeek(
  config: RecurringTasksConfig,
  runtime: { week: unknown; days: Record<DayKey, unknown> },
): number {
  // Both modes score entirely through their days, so the seven day tiles
  // always reconcile with the week total.
  let total = 0;
  for (const k of DAY_KEYS) {
    total += scoreDay(config, runtime.days[k], k, runtime.week);
  }
  return total;
}

interface PerDayBreakdown {
  dayKey: DayKey;
  taskIds: string[];
  hasSet: boolean;
}

export function dailyBreakdown(
  config: DailyRecurringTasksConfig,
): PerDayBreakdown[] {
  return DAY_KEYS.map((dayKey) => {
    const set = tasksForDay(config, dayKey);
    return {
      dayKey,
      taskIds: set.map((t) => t.id),
      hasSet: set.length > 0,
    };
  });
}

export function maxScoreWeek(config: RecurringTasksConfig): number {
  if (config.reset === "weekly") {
    return config.tasks.length === 0 ? 0 : config.points;
  }
  return dailyBreakdown(config).filter((b) => b.hasSet).length * config.points;
}
